import { useMemo } from 'react';

import { getInfo, keyOf } from '@Components/Grid/components/Table/formatters';
import column, { defaultActionsColumn, defaultColumnId } from '@Components/Grid/utils/column';

import CancelFormatter from './CancelFormatter';
import DateFormatter from './DateFormatter';
import DeleteFormatter from './DeleteFormatter';
import DetailsFormatter from './DetailsFormatter';
import DownloadFormatter from './DownloadFormatter';
import ProgressFormatter from './ProgressFormatter';
import RetryFormatter from './RetryFormatter';
import SiteLinkFormatter from './SiteLinkFormatter';
import StatusFormatter from './StatusFormatter';
import TypeFormatter from './TypeFormatter';
import UserLinkFormatter from './UserLinkFormatter';

export default function useColumns() {
  return useMemo(() => [
    defaultColumnId(),
    column.accessor('userId', {
      header: 'User',
      cell: info => <UserLinkFormatter info={info} />,
      enableSorting: false,
    }),
    column.accessor('siteId', {
      header: 'Site',
      cell: info => <SiteLinkFormatter info={info} />,
      enableSorting: false,
    }),
    column.accessor('dataType', {
      header: 'Type',
      cell: info => <TypeFormatter info={info} />,
    }),
    column.accessor('meta.configuration.startAt', {
      header: 'Start At',
      cell: info => <DateFormatter info={info} />,
      enableSorting: false,
    }),
    column.accessor('meta.configuration.endAt', {
      header: 'End At',
      cell: info => <DateFormatter info={info} />,
      enableSorting: false,
    }),
    column.accessor('status', {
      header: 'Status',
      cell: info => <StatusFormatter info={info} />,
      enableSorting: false,
    }),
    column.accessor('progress', {
      header: 'Progress',
      cell: info => <ProgressFormatter info={info} />,
      enableSorting: true,
      maxSize: 50,
    }),
    defaultActionsColumn(detailsAction, downloadAction, cancelAction, retryAction, deleteAction),
  ], [])
}

function detailsAction(info) {
  return <DetailsFormatter key={keyOf(info, 'details')} info={info} />
}

function downloadAction(info) {
  const { row } = getInfo(info)
  const { status, meta: { file } } = row
  const { filekey } = file || {}

  const downloadAvailable = status === 'finished' || (status === 'error' && filekey)
  if (!downloadAvailable) {
    return null
  }

  return <DownloadFormatter key={keyOf(info, 'download')} info={info} />
}

function cancelAction(info) {
  const { row } = getInfo(info)
  const { status } = row

  const cancelAvailable = ['to_process', 'waiting', 'processing'].includes(status)
  if (!cancelAvailable) {
    return null
  }

  return <CancelFormatter key={keyOf(info, 'cancel')} info={info} />
}

function deleteAction(info) {
  const { row } = getInfo(info)
  const { status } = row

  const deleteAvailable = ['finished', 'expired', 'canceled', 'error'].includes(status)
  if (!deleteAvailable) {
    return null
  }

  return <DeleteFormatter key={keyOf(info, 'delete')} info={info} />
}

function retryAction(info) {
  const { row } = getInfo(info)
  const { status, meta: { file } } = row
  const { filekey } = file || {}

  const retryAvailable = ['canceled', 'error'].includes(status) && !filekey
  if (!retryAvailable) {
    return null
  }

  return <RetryFormatter key={keyOf(info, 'retry')} info={info} />
}
